import React, { FC, ReactElement, ReactNode } from 'react';
import { css, styled } from '../../styles';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { BpDesignSystem } from '../../types';
import { filterNotEmpty } from '../../utils/listHelpers';
import { PrimaryButton } from '../Button/Button';
import { Link } from '../Link/Link';
import { ListItem } from '../List/ListItem';
import { Typography } from '../Typography/Typography';
import { NotificationIconType } from './Notification.types';
import { NotificationImage } from './NotificationImage';

const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

export type NotificationProps = BpDesignSystem.PropsWithChildren<{
  className?: string;
  date: string;
  description?: string;
  resourceLocation?: string | undefined | null;
  uid: string;
  read: boolean;
  clickable?: boolean;
  title: string;
  to?: string;
  iconType?: NotificationIconType;
  onDismiss?: () => void;
  onClick?: () => void;
  onResourceDownload?: () => void;
  actions?: ReactElement | ReactElement[];
  replaceLinkInDescription?: string;
  /* ! @default: false */
  alwaysShowActions?: boolean;
  LinkComponent?: FC<{
    to: string;
    style: React.CSSProperties;
    children: ReactNode;
  }>;
}>;

const ConditionalLink: FC<Pick<NotificationProps, 'to' | 'LinkComponent' | 'children'>> = ({
  to,
  children,
  LinkComponent,
  ...props
}) => {
  return to && LinkComponent ? (
    <LinkComponent to={to} style={{ textDecoration: 'none' }} {...props}>
      {children}
    </LinkComponent>
  ) : (
    children
  );
};

export const Notification: FC<NotificationProps> = ({
  date,
  description,
  uid,
  read,
  title,
  to,
  iconType,
  onDismiss,
  resourceLocation,
  LinkComponent,
  onResourceDownload,
  onClick,
  dataTest,
  actions,
  alwaysShowActions = false,
  replaceLinkInDescription,
  clickable,
  ...props
}) => {
  const downloadHandler = !!resourceLocation && !!onResourceDownload ? () => onResourceDownload?.() : undefined;
  const { intl } = useIntlBds();

  const linkReplaceText = replaceLinkInDescription ?? intl.Notification.seeDetailsButtonLabel;
  const splitDescription = filterNotEmpty(description?.split(/\s/));
  const replacedDescription = description?.includes('https://')
    ? splitDescription.map((it, index) => {
        if (it.startsWith('https://')) {
          let href = it;
          if (IS_DEVELOPMENT) {
            const url = new URL(it);
            href = href.replace(url.origin, window.location.origin);
          }
          return (
            <Link key={href} display="inline-block" href={href} color="primary">
              {linkReplaceText}
            </Link>
          );
        }
        const isLast = splitDescription.length - 1 === index;
        return `${it}${isLast ? '' : ' '}`;
      })
    : description;

  const hasActions = !!actions || !!onDismiss;
  return (
    <ConditionalLink to={to} LinkComponent={LinkComponent} {...props}>
      <StyledListItemStyled
        $clickable={clickable || !!to}
        key={uid}
        onClick={() => {
          downloadHandler?.();
          onClick?.();
        }}
        dataTest={dataTest}
        {...(LinkComponent || !to ? props : {})}
      >
        {read && <StyledBorder />}
        <StyledContent $alwaysShowActions={alwaysShowActions}>
          <NotificationImage type={iconType} />
          <StyledTextContainer>
            <Typography dataTest="notification-title">{title}</Typography>

            {!!replacedDescription?.length && (
              <Typography dataTest="notification-description" variant="small">
                {replacedDescription}
              </Typography>
            )}
            {hasActions && (
              <StyledActions data-test="notification-actions">
                {actions}
                {onDismiss && (
                  <PrimaryButton.Text
                    size="small"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onDismiss();
                    }}
                    dataTest="notification-dismiss-button"
                  >
                    {intl.Notification.dismissButtonLabel}
                  </PrimaryButton.Text>
                )}
              </StyledActions>
            )}

            {date && (
              <Typography variant="small" dataTest="notification-date" style={{ marginTop: '0.6rem' }}>
                {date}
              </Typography>
            )}
          </StyledTextContainer>
        </StyledContent>
      </StyledListItemStyled>
    </ConditionalLink>
  );
};

const StyledListItemStyled = styled(ListItem)<{ $clickable: boolean }>`
  position: relative !important;
  transition: 0.15s background ease-in-out;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  
  &:hover {
    background: #efefef;
  }
`;

const StyledActions = styled('div')`
  margin-left: auto;
  margin-top: auto;
  position: absolute;
  display: flex;
  right: 0;
  bottom: 0;
`;

const StyledBorder = styled('div')`
  position: absolute;
  left: 0;
  border-left: 3px solid ${({ theme }) => theme.tokens.colorWarning};
  height: 100%;
`;

const hideButtonStyles = css`
  .MuiButtonBase-root {
    display: none;
  }
  &:hover {
    .MuiButtonBase-root {
      display: inline-flex;
    }
  }
`;

const StyledContent = styled('div')<{ $alwaysShowActions: boolean }>`
  width: 100%;
  display: flex;
  flex-grow: 1;
  ${({ $alwaysShowActions }) => ($alwaysShowActions ? '' : hideButtonStyles)}
`;

const StyledTextContainer = styled('div')`
  width: 100%;
  margin-left: 1rem;
  display: flex;
  flex-direction: column;
  > * {
    margin-bottom: 3px;
  }
`;
